"use client";

import { Product } from "../../components/item/product";
import { ProductSlim } from "~/use-cases/contracts/Product";
import { useEffect, useRef, useState } from "react";
import { useAppContext } from "~/ui/app-context/provider";
import search from "~/use-cases/crystallize/read/search";
import { DataMapper } from "~/use-cases/mapper";
import Spinner from "../aplia/Spinner";

let observer: IntersectionObserver | null = null;

export const SearchProducts: React.FC<{
  initialProducts: (ProductSlim & { paginationToken: string })[];
  searchTerm: string;
}> = ({ initialProducts, searchTerm }) => {
  const [products, setProducts] = useState(initialProducts);
  const [isLoading, setIsLoading] = useState(false);
  const { state: appContextState } = useAppContext();

  const endOfListElement = useRef(null);

  useEffect(() => {
    if (observer !== null) {
      observer.disconnect();
    }
    if (endOfListElement.current === null) return;

    let isIntersecting = false;
    let _isLoading = false;

    observer = new IntersectionObserver(
      async (entries) => {
        isIntersecting = entries[entries.length - 1].isIntersecting;
        if (isIntersecting && !_isLoading) {
          _isLoading = true;
          setIsLoading(true);

          const nextProducts = await search(
            searchTerm,
            appContextState.language,
            products[products.length - 1].paginationToken,
          );
          if (nextProducts.length === 0) {
            observer?.disconnect();
            setIsLoading(false);
            return;
          }

          setProducts([
            ...products,
            ...DataMapper(appContextState).API.Call.searchProductToProductSlim(
              nextProducts,
            ),
          ]);
          setIsLoading(false);
        }
      },
      {
        threshold: 0.1,
        rootMargin: "20px 0px 0px 0px",
      },
    );
    observer.observe(endOfListElement.current);
  }, [endOfListElement, products]);

  return (
    <div className="mt-10 mb-20">
      <div className="flex justify-between items-center">
        {products?.length < 1 && ( // Only shop when 0 results
          <h2 className="font-medium text-md my-5 text-center w-full">
            Fant {products?.length} samsvarende resultat
          </h2>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-6 gap-y-8 md:gap-y-16">
        {products.map((product, index) => (
          <Product key={index} item={product} />
        ))}
      </div>
      <div className="h-1" ref={endOfListElement}></div>
      {isLoading && <Spinner></Spinner>}
    </div>
  );
};
